import { AdminTitle } from "@/admin/AdminTitle.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { iftuneQueryAtom, updateIftuneMutation } from "@/admin/vm/iftuneQuery.ts";
import { averageDescription, burstDescription, peakDescription } from "@/admin/vm/iftuneUnits.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import FF from "@/components/forms/FormField.module.css";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { IftuneConfBody } from "@warrenio/api-spec/spec.oats.gen";
import { useState } from "react";
import { Link } from "react-aria-components";

export function IftuneConfView() {
    const conf = useSuspenseQueryAtom(iftuneQueryAtom);
    const modifyMutation = useStandardMutation(updateIftuneMutation);

    const [unlimited, setUnlimited] = useState<boolean>(conf?.average_kbps === 0);
    const [average, setAverage] = useState<string | number>(conf?.average_kbps ?? "");
    const [peak, setPeak] = useState<string | number>(conf?.peak_kbps ?? "");
    const [burst, setBurst] = useState<string | number>(conf?.burst_kb ?? "");

    async function onSubmit() {
        const confBody: IftuneConfBody = unlimited
            ? { average_kbps: 0 }
            : {
                  average_kbps: average ? Number(average) : 0,
              };

        if (!unlimited) {
            if (Number(peak)) confBody.peak_kbps = Number(peak);
            if (Number(burst)) confBody.burst_kb = Number(burst);
        }

        await modifyMutation.mutateAsync({
            body: confBody,
        });
    }

    return (
        <>
            <AdminTitle title="Network" />

            <div className="p-2">
                <p className="pb-3 color-muted">
                    Bandwidth limits that apply to all network interfaces of virtual machines. This includes all traffic
                    that goes through the network interface, there is no distinction between internal and external
                    connections. The same limits are applied to both inbound and outbound connections. This setting
                    becomes effective for VMs that are started after the configuration is changed. When setting new
                    limits, it is advised to set the limits to some test VM first to see if the numbers are acceptable
                    to libvirt. See{" "}
                    <Link href="https://libvirt.org/formatnetwork.html#quality-of-service" target="_blank">
                        libvirt network doc
                    </Link>{" "}
                    for more information about specific iftune parameters.
                </p>
            </div>

            <div className="p-4 pt-0 pb-0">
                <WCheckbox isSelected={unlimited} onChange={setUnlimited}>
                    Unlimited (value 0 equals to unlimited)
                </WCheckbox>
            </div>

            <WTextField
                isReadOnly={unlimited}
                className="max-w-24rem"
                wide
                label="Average (KiB/s)"
                onChange={setAverage}
                description={averageDescription(average)}
            >
                <AInput value={average} type="number" min={0} step={1} />
            </WTextField>

            <WTextField
                isReadOnly={unlimited}
                className="max-w-24rem"
                wide
                label="Peak (KiB/s)"
                onChange={setPeak}
                description={peakDescription(peak)}
            >
                <AInput value={peak} type="number" min={0} step={1} />
            </WTextField>

            <WTextField
                isReadOnly={unlimited}
                className="max-w-24rem"
                wide
                label="Burst (KiB)"
                onChange={setBurst}
                description={burstDescription(burst)}
            >
                <AInput value={burst} type="number" min={0} step={1} />
            </WTextField>

            <Separator />

            <div className={FF.FormActions}>
                <WButton color="primary" variant="basic" size="md" action={async () => await onSubmit()}>
                    Save
                </WButton>
            </div>
        </>
    );
}
